import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowLeft, ArrowRight, Check, ChevronDown, FileText, Upload } from "lucide-react";
import { BRIEF_SECTIONS, PRIVACY_NOTE } from "./config";
import { LogoMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isEmpty = (v) =>
  v === undefined ||
  v === null ||
  (typeof v === "string" && !v.trim()) ||
  (Array.isArray(v) && v.length === 0);

const visibleFields = (section, data) =>
  section.fields.filter((f) => !f.visibleWhen || data[f.visibleWhen.field] === f.visibleWhen.equals);

const validateSection = (section, data, files) => {
  const errors = {};
  visibleFields(section, data).forEach((f) => {
    if (!f.required) return;
    if (f.type === "file") {
      if (!files[f.id] || files[f.id].length === 0) errors[f.id] = "Please attach at least one file.";
    } else if (isEmpty(data[f.id])) {
      errors[f.id] = "This field is required.";
    } else if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data[f.id])) {
      errors[f.id] = "Please enter a valid email address.";
    }
  });
  return errors;
};

const FieldLabel = ({ f }) => (
  <span className="mb-2 flex items-baseline justify-between gap-4">
    <span className="text-[15px] text-ivory/90">{f.label}</span>
    <span
      className={`shrink-0 font-mono text-[9px] uppercase tracking-[0.18em] ${
        f.required ? "text-gold/70" : "text-mist/40"
      }`}
    >
      {f.required ? "Required" : "Optional"}
    </span>
  </span>
);

const ErrorNote = ({ msg }) => (
  <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-terra" role="alert">
    <AlertCircle size={12} aria-hidden="true" />
    {msg}
  </p>
);

const Field = ({ f, data, files, errors, setData, setFiles }) => {
  const value = data[f.id] ?? "";
  const set = (v) => setData((d) => ({ ...d, [f.id]: v }));
  const err = errors[f.id];

  let control = null;
  if (f.type === "text" || f.type === "email" || f.type === "tel") {
    control = (
      <input
        type={f.type}
        className="field"
        value={value}
        placeholder={f.placeholder || ""}
        onChange={(e) => set(e.target.value)}
        data-testid={`field-${f.id}`}
      />
    );
  } else if (f.type === "textarea") {
    control = (
      <textarea
        className="field min-h-[110px] resize-y"
        value={value}
        placeholder={f.placeholder || ""}
        onChange={(e) => set(e.target.value)}
        data-testid={`field-${f.id}`}
      />
    );
  } else if (f.type === "select") {
    control = (
      <span className="relative block">
        <select
          className="field appearance-none pr-10"
          value={value}
          onChange={(e) => set(e.target.value)}
          data-testid={`field-${f.id}`}
        >
          <option value="">— Select —</option>
          {f.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <ChevronDown size={15} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gold/70" aria-hidden="true" />
      </span>
    );
  } else if (f.type === "radio") {
    control = (
      <div className="flex border border-mist/20" role="radiogroup" aria-label={f.label} data-testid={`field-${f.id}`}>
        {f.options.map((o, i) => (
          <button
            key={o}
            type="button"
            role="radio"
            aria-checked={value === o}
            onClick={() => set(o)}
            data-testid={`field-${f.id}-${o.toLowerCase().replace(/\s+/g, "-")}`}
            className={`flex-1 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-200 ${
              i > 0 ? "border-l border-mist/20" : ""
            } ${value === o ? "bg-gold/15 text-gold" : "text-mist hover:text-ivory"}`}
          >
            {o}
          </button>
        ))}
      </div>
    );
  } else if (f.type === "checkbox-group") {
    const selected = data[f.id] ?? [];
    const toggle = (o) =>
      set(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
    control = (
      <div className="flex flex-wrap gap-2" role="group" aria-label={f.label} data-testid={`field-${f.id}`}>
        {f.options.map((o) => {
          const active = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(o)}
              data-testid={`field-${f.id}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`flex items-center gap-2 border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-200 ${
                active
                  ? "border-gold/70 bg-gold/10 text-gold"
                  : "border-mist/25 text-mist hover:border-mist/50 hover:text-ivory"
              }`}
            >
              {active && <Check size={12} aria-hidden="true" />}
              {o}
            </button>
          );
        })}
      </div>
    );
  } else if (f.type === "file") {
    const list = files[f.id] ?? [];
    control = (
      <div data-testid={`field-${f.id}`}>
        <label
          className="flex cursor-pointer flex-col items-center gap-3 border border-dashed border-gold/30 px-4 py-7 text-center transition-colors duration-200 hover:border-gold/60 hover:bg-gold/[0.04]"
          data-testid={`field-${f.id}-dropzone`}
        >
          <Upload size={18} className="text-gold" aria-hidden="true" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
            Click to attach files
          </span>
          <input
            type="file"
            multiple={f.multiple}
            className="sr-only"
            onChange={(e) => setFiles((m) => ({ ...m, [f.id]: Array.from(e.target.files) }))}
          />
        </label>
        {f.help && <p className="mt-2 text-xs text-mist/60">{f.help}</p>}
        {list.length > 0 && (
          <ul className="mt-3 space-y-2">
            {list.map((file, i) => (
              <li key={i} className="flex items-center gap-3 border border-gold/15 bg-abyss/60 px-4 py-2.5 text-sm text-ivory/85" data-testid={`field-${f.id}-file-${i}`}>
                <FileText size={13} className="shrink-0 text-gold" aria-hidden="true" />
                <span className="truncate">{file.name}</span>
                <span className="ml-auto shrink-0 font-mono text-[10px] text-mist/60">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  } else if (f.type === "consent") {
    control = (
      <label className="flex cursor-pointer items-start gap-4">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => set(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 accent-[#C9A24B]"
          data-testid={`field-${f.id}`}
        />
        <span className="text-sm leading-relaxed text-ivory/85">{f.label}</span>
      </label>
    );
  }

  if (f.type === "competitors") {
    const rows = data[f.id] ?? [{ name: "", origin: "", notes: "" }, { name: "", origin: "", notes: "" }, { name: "", origin: "", notes: "" }];
    const setRow = (i, key, v) => {
      const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r));
      set(next);
    };
    control = (
      <div className="space-y-4" data-testid={`field-${f.id}`}>
        {rows.map((row, i) => (
          <div key={i} className="grid gap-4 border border-gold/15 bg-abyss/40 p-5 md:grid-cols-[2fr_1fr_3fr]">
            <div>
              <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.2em] text-mist/50">
                Competitor {String(i + 1).padStart(2, "0")} — name
              </p>
              <input
                className="field"
                value={row.name}
                onChange={(e) => setRow(i, "name", e.target.value)}
                data-testid={`field-${f.id}-name-${i + 1}`}
                aria-label={`Competitor ${i + 1} name`}
              />
            </div>
            <div>
              <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.2em] text-mist/50">Origin</p>
              <span className="relative block">
                <select
                  className="field appearance-none pr-9"
                  value={row.origin}
                  onChange={(e) => setRow(i, "origin", e.target.value)}
                  data-testid={`field-${f.id}-origin-${i + 1}`}
                  aria-label={`Competitor ${i + 1} origin`}
                >
                  <option value="">—</option>
                  <option value="India">India</option>
                  <option value="Global">Global</option>
                  <option value="Unknown">Unknown</option>
                </select>
                <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gold/70" aria-hidden="true" />
              </span>
            </div>
            <div>
              <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.2em] text-mist/50">
                Positioning / pricing notes
              </p>
              <input
                className="field"
                value={row.notes}
                onChange={(e) => setRow(i, "notes", e.target.value)}
                data-testid={`field-${f.id}-notes-${i + 1}`}
                aria-label={`Competitor ${i + 1} notes`}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (f.type === "consent") {
    return (
      <div className="col-span-full">
        {control}
        {err && <ErrorNote msg={err} />}
      </div>
    );
  }

  return (
    <div className={f.full ? "md:col-span-2" : ""}>
      {f.type !== "file" && f.type !== "competitors" && <FieldLabel f={f} />}
      {f.type === "file" && <FieldLabel f={f} />}
      {control}
      {f.type === "competitors" && <p className="mt-2 text-xs text-mist/60">Leave blank if unknown — ENEXUR will verify.</p>}
      {err && <ErrorNote msg={err} />}
    </div>
  );
};

export const BriefForm = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const section = BRIEF_SECTIONS[step];
  const isLast = step === BRIEF_SECTIONS.length - 1;

  const goNext = () => {
    const errs = validateSection(section, data, files);
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      setStep((s) => Math.min(s + 1, BRIEF_SECTIONS.length - 1));
      if (window.__lenis) window.__lenis.scrollTo(0);
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goBack = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    const allErrors = {};
    BRIEF_SECTIONS.forEach((s) => Object.assign(allErrors, validateSection(s, data, files)));
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      const firstIdx = BRIEF_SECTIONS.findIndex((s) =>
        visibleFields(s, data).some((f) => allErrors[f.id])
      );
      if (firstIdx >= 0) setStep(firstIdx);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ ...data, submitted_from: window.location.href }));
      Object.entries(files).forEach(([fid, arr]) =>
        (arr ?? []).forEach((file) => fd.append(fid, file))
      );
      const res = await fetch(`${API}/client-brief`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Rejected");
      setResult(json);
      if (window.__lenis) window.__lenis.scrollTo(0);
      else window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setSubmitError(
        "We could not accept the submission just now. Please try again, or write to business@enexur.in."
      );
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="border border-gold/30 bg-abyss/60 p-10 text-center md:p-16" data-testid="brief-success-panel">
        <LogoMark className="mx-auto h-12 w-12" />
        <h2 className="mt-8 font-display text-3xl text-ivory md:text-4xl">
          Your Client Intelligence Brief has been received.
        </h2>
        <p className="mx-auto mt-5 max-w-md text-[15px] leading-relaxed text-mist">
          Recorded under reference{" "}
          <span className="font-mono text-gold">{result.reference}</span>. The ENEXUR
          team will respond to the contact email you provided.
        </p>
        <Link to="/india-market-intelligence" className="btn-ghost mt-10" data-testid="brief-success-return">
          Return to the product page
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* stepper */}
      <div className="flex items-start overflow-x-auto pb-2 no-scrollbar" data-testid="brief-stepper">
        {BRIEF_SECTIONS.map((s, i) => (
          <div key={s.id} className="flex items-start">
            <button
              onClick={() => { setStep(i); setErrors({}); }}
              className="group flex flex-col items-center px-1 sm:px-3"
              data-testid={`brief-step-${i + 1}`}
              aria-current={i === step ? "step" : undefined}
            >
              <span
                className={`flex h-9 w-9 rotate-45 items-center justify-center border font-mono text-[10px] transition-colors duration-300 ${
                  i === step
                    ? "border-gold bg-gold/15 text-gold"
                    : i < step
                      ? "border-gold/50 text-gold/80"
                      : "border-mist/25 text-mist/50 group-hover:border-gold/50"
                }`}
              >
                <span className="-rotate-45">{i < step ? "✓" : s.num}</span>
              </span>
              <span
                className={`mt-3 hidden whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.18em] md:block ${
                  i === step ? "text-gold" : "text-mist/50"
                }`}
              >
                {s.title}
              </span>
            </button>
            {i < BRIEF_SECTIONS.length - 1 && (
              <span className="mt-[18px] h-px w-7 bg-gold/20 sm:w-12" aria-hidden="true" />
            )}
          </div>
        ))}
      </div>

      {/* panel */}
      <div className="mt-12 border border-gold/20 bg-abyss/50 p-6 md:p-12" data-testid="brief-form-panel">
        <div className="flex items-baseline justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              Section {section.num} / 07
            </p>
            <h2 className="mt-4 font-display text-3xl text-ivory md:text-4xl">{section.title}</h2>
            <p className="mt-3 text-[15px] text-mist">{section.blurb}</p>
          </div>
          <span className="hidden shrink-0 font-display text-7xl text-gold/10 md:block" aria-hidden="true">
            {section.num}
          </span>
        </div>

        <div className="mt-10 grid gap-7 md:grid-cols-2">
          {visibleFields(section, data).map((f) => (
            <Field
              key={f.id}
              f={f}
              data={data}
              files={files}
              errors={errors}
              setData={setData}
              setFiles={setFiles}
            />
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-6 border-t border-gold/12 pt-8">
          <div className="flex items-center justify-between gap-4">
            {step > 0 ? (
              <button onClick={goBack} className="btn-ghost" data-testid="brief-back-button" disabled={submitting}>
                <ArrowLeft size={14} aria-hidden="true" />
                Back
              </button>
            ) : (
              <span />
            )}
            {isLast ? (
              <button onClick={submit} className="btn-gold" data-testid="brief-submit-button" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit the brief"}
              </button>
            ) : (
              <button onClick={goNext} className="btn-gold" data-testid="brief-next-button">
                Next — {BRIEF_SECTIONS[step + 1].title}
                <ArrowRight size={14} aria-hidden="true" />
              </button>
            )}
          </div>
          {submitError && (
            <p className="flex items-center gap-3 border border-terra/40 bg-terra/10 px-5 py-4 text-sm text-terra" role="alert" data-testid="brief-submit-error">
              <AlertCircle size={15} aria-hidden="true" />
              {submitError}
            </p>
          )}
          <p className="text-xs leading-relaxed text-mist/70">{PRIVACY_NOTE}</p>
        </div>
      </div>
    </div>
  );
};
